import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PageHeader, KpiCard, DataCheckChip, PresetGroup, KpiCardSkeleton } from '../components/ds';
import { formatCurrency as safeFormatCurrency } from '../utils/currency';
import { useAuth } from '../context/AuthContext';
import { todayIST, daysAgoIST, monthStartIST, yearStartIST } from '../utils/istDate';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  Clock,
  Target,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  Filter,
  Activity,
  CheckCircle,
  XCircle,
  Radio
} from 'lucide-react';
import { getApiUrl } from '../utils/api';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const API_URL = getApiUrl();

const Dashboard = () => {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [adminOverview, setAdminOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Date range state (IST wall-clock dates; backend interprets as IST boundaries)
  const [dateRange, setDateRange] = useState({
    start_date: daysAgoIST(30),
    end_date: todayIST()
  });
  
  // Source filter
  const [selectedSource, setSelectedSource] = useState('');
  
  // Quick date presets
  const [activePreset, setActivePreset] = useState('30d');

  useEffect(() => {
    fetchMetrics();
    if (user?.role === 'super_admin' || user?.role === 'finance_admin') {
      fetchAdminOverview();
    }
  }, [dateRange, selectedSource, user]);

  const fetchMetrics = async () => {
    try {
      const params = new URLSearchParams();
      if (dateRange.start_date) params.append('start_date', dateRange.start_date);
      if (dateRange.end_date) params.append('end_date', dateRange.end_date);
      
      const response = await axios.get(`${API_URL}/api/analytics/dashboard?${params}`, {
        withCredentials: true
      });
      setMetrics(response.data);
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminOverview = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (dateRange.start_date) params.append('start_date', dateRange.start_date);
      if (dateRange.end_date) params.append('end_date', dateRange.end_date);
      if (selectedSource) params.append('source', selectedSource);
      
      const response = await axios.get(`${API_URL}/api/analytics/super-admin-overview?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminOverview(response.data);
    } catch (error) {
      console.error('Error fetching admin overview:', error);
    }
  };

  const applyPreset = (preset) => {
    setActivePreset(preset);
    let startDate;

    switch (preset) {
      case '7d':
        startDate = daysAgoIST(7);
        break;
      case '14d':
        startDate = daysAgoIST(14);
        break;
      case '30d':
        startDate = daysAgoIST(30);
        break;
      case 'mtd':
        startDate = monthStartIST();
        break;
      case 'ytd':
        startDate = yearStartIST();
        break;
      default:
        startDate = daysAgoIST(30);
    }

    setDateRange({
      start_date: startDate,
      end_date: todayIST()
    });
  };

  const formatCurrency = (amount, currency = 'INR') => safeFormatCurrency(amount, currency, { maximumFractionDigits: 0 });

  // Use real data from API
  const revenueData = metrics?.revenue_trend?.map(item => ({
    name: item.day,
    value: item.value,
    date: item.date
  })) || [];

  const statusData = [
    { name: 'Paid', value: metrics?.status_counts?.paid || 0, color: '#10B981' },
    { name: 'Pending', value: metrics?.status_counts?.pending || 0, color: '#F59E0B' },
    { name: 'Expired', value: metrics?.status_counts?.expired || 0, color: '#EF4444' }
  ];

  // Check if there's any data
  const hasRevenueData = revenueData.some(d => d.value > 0);
  const hasStatusData = statusData.some(d => d.value > 0);

  if (loading) {
    return (
      <div data-testid="dashboard-loading" className="space-y-6">
        <div className="h-9 w-64 shimmer rounded-md" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {Array.from({ length: 5 }).map((_, i) => <KpiCardSkeleton key={i} />)}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-6 animate-fade-in">
      {/* Header with Date Filter */}
      <PageHeader
        title="Overall Dashboard"
        description={`Welcome back, ${user?.full_name?.split(' ')[0] || ''}`}
        actions={(
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <DataCheckChip startDate={dateRange.start_date} endDate={dateRange.end_date} />
          <PresetGroup
            testIdPrefix="dashboard-preset"
            value={activePreset}
            onChange={applyPreset}
            options={[
              { key: '7d', label: '7D' },
              { key: '14d', label: '14D' },
              { key: '30d', label: '30D' },
              { key: 'mtd', label: 'MTD' },
              { key: 'ytd', label: 'YTD' }
            ]}
          />
          
          {/* Custom Date Range */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-1.5" data-testid="filter-date-range-picker">
              <Calendar className="w-4 h-4 text-slate-400" />
              <input
                type="date"
                value={dateRange.start_date}
                onChange={(e) => {
                  setActivePreset('custom');
                  setDateRange({ ...dateRange, start_date: e.target.value });
                }}
                className="text-sm border-none outline-none bg-transparent w-[110px]"
              />
              <span className="text-slate-400">-</span>
              <input
                type="date"
                value={dateRange.end_date}
                onChange={(e) => {
                  setActivePreset('custom');
                  setDateRange({ ...dateRange, end_date: e.target.value });
                }}
                className="text-sm border-none outline-none bg-transparent w-[110px]"
              />
            </div>
          </div>
          
          {/* Source Filter (Admin only) */}
          {(user?.role === 'super_admin' || user?.role === 'finance_admin') && (
            <select
              value={selectedSource}
              onChange={(e) => setSelectedSource(e.target.value)}
              className="input-base h-9 w-auto"
              data-testid="source-filter"
            >
              <option value="">All Sources</option>
              <option value="manual">Manual Only</option>
            </select>
          )}
        </div>
        )}
      />

      {/* Metrics Grid - INR */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 stagger">
        <MetricCard
          title="Today (INR)"
          value={formatCurrency(metrics?.inr?.today ?? metrics?.today_collection ?? 0)}
          icon={DollarSign}
        />
        <MetricCard
          title="Selected Range (INR)"
          value={formatCurrency(metrics?.inr?.range ?? metrics?.range_collection ?? 0)}
          icon={TrendingUp}
          highlight
          subtitle={metrics?.inr?.range_refunds > 0 ? `Net: ${formatCurrency(metrics?.inr?.range_net || 0)}` : null}
        />
        <MetricCard
          title="MTD (INR)"
          value={formatCurrency(metrics?.inr?.mtd ?? metrics?.mtd_collection ?? 0)}
          icon={Calendar}
        />
        <MetricCard
          title="YTD (INR)"
          value={formatCurrency(metrics?.inr?.ytd ?? metrics?.ytd_collection ?? 0)}
          icon={TrendingUp}
        />
        <MetricCard
          title="Pending Payment Links"
          value={metrics?.pending_payment_links_count ?? metrics?.pending_payments_count ?? 0}
          subtitle={formatCurrency(metrics?.pending_payment_links_value_inr ?? metrics?.pending_payments_value ?? 0)}
          icon={Clock}
        />
      </div>

      {/* USD Metrics */}
      {(metrics?.usd?.today > 0 || metrics?.usd?.mtd > 0 || metrics?.usd?.ytd > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Today (USD)"
            value={`$${(metrics?.usd?.today || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}`}
            icon={DollarSign}
          />
          <MetricCard
            title="Range (USD)"
            value={`$${(metrics?.usd?.range || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}`}
            icon={TrendingUp}
          />
          <MetricCard
            title="MTD (USD)"
            value={`$${(metrics?.usd?.mtd || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}`}
            icon={Calendar}
          />
          <MetricCard
            title="YTD (USD)"
            value={`$${(metrics?.usd?.ytd || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}`}
            icon={TrendingUp}
          />
        </div>
      )}

      {/* Conversion Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Lead → Enrollment</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{metrics?.lead_to_enrollment_rate || 0}%</p>
                <p className="text-xs text-slate-400 mt-1">Full funnel conversion</p>
              </div>
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center">
                <Target className="w-8 h-8 text-indigo-600" />
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Total Leads</span>
                <span className="font-medium">{metrics?.total_leads || 0}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className="text-slate-500">Enrolled</span>
                <span className="font-medium text-green-600">{metrics?.enrolled_leads || 0}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Consultation → Enrollment</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{metrics?.consultation_to_enrollment_rate || 0}%</p>
                <p className="text-xs text-slate-400 mt-1">Post-consultation conversion</p>
              </div>
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center">
                <TrendingUp className="w-8 h-8 text-emerald-600" />
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Consultations Completed</span>
                <span className="font-medium" data-testid="metric-consultations-completed">{metrics?.consultations_completed || 0}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className="text-slate-500">Converted to Enrolled</span>
                <span className="font-medium text-green-600" data-testid="metric-enrolled-from-consultation">{metrics?.enrolled_from_consultation || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Trend */}
        <div className="lg:col-span-2 card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-slate-900">Revenue Trend</h3>
            <p className="text-sm text-slate-500">
              {dateRange.start_date} to {dateRange.end_date}
            </p>
          </div>
          <div className="card-body">
            {hasRevenueData ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} tickFormatter={(value) => `₹${value.toLocaleString()}`} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }}
                    formatter={(value) => [`₹${value.toLocaleString()}`, 'Revenue']}
                    labelFormatter={(label, payload) => payload?.[0]?.payload?.date || label}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#4f46e5" 
                    strokeWidth={2}
                    dot={{ fill: '#4f46e5', strokeWidth: 2 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <DollarSign className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No revenue data for selected period</p>
                  <p className="text-sm">Try selecting a different date range</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Payment Status */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-slate-900">Payment Status</h3>
            <p className="text-sm text-slate-500">Distribution</p>
          </div>
          <div className="card-body flex flex-col items-center">
            {hasStatusData ? (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={statusData.filter(d => d.value > 0)}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {statusData.filter(d => d.value > 0).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [value, 'Count']} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex gap-4 mt-4">
                  {statusData.filter(d => d.value > 0).map((item) => (
                    <div key={item.name} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-sm text-slate-600">{item.name} ({item.value})</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <p>No payment data for selected period</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Leads</p>
                <p className="text-2xl font-bold text-slate-900">{metrics?.total_leads || 0}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Target className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Enrolled</p>
                <p className="text-2xl font-bold text-slate-900">{metrics?.enrolled_leads || 0}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Avg. Ticket Size</p>
                <p className="text-2xl font-bold text-slate-900">{formatCurrency(metrics?.avg_ticket_size || 0)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Team Performance (for super_admin and finance_admin) */}
      {adminOverview && (user?.role === 'super_admin' || user?.role === 'finance_admin') && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pre-Sales Leaderboard */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-slate-900">Pre-Sales Leaderboard</h3>
              <a href="/presales-dashboard" className="text-sm text-violet-600 hover:text-violet-700">View Details →</a>
            </div>
            <div className="card-body">
              {adminOverview.presales_leaderboard?.length > 0 ? (
                <div className="space-y-3">
                  {adminOverview.presales_leaderboard.map((ps, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="text-lg">
                          {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `${idx + 1}.`}
                        </span>
                        <span className="font-medium text-slate-900">{ps.name}</span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-slate-500">{ps.leads} leads</span>
                        <span className="font-semibold text-emerald-600">{ps.enrolled} enrolled</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-400 py-4">No data for selected period</p>
              )}
            </div>
          </div>

          {/* Sales Leaderboard */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-slate-900">Sales Leaderboard</h3>
              <a href="/sales-dashboard" className="text-sm text-violet-600 hover:text-violet-700">View Details →</a>
            </div>
            <div className="card-body">
              {adminOverview.sales_leaderboard?.length > 0 ? (
                <div className="space-y-3">
                  {adminOverview.sales_leaderboard.map((s, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="text-lg">
                          {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `${idx + 1}.`}
                        </span>
                        <span className="font-medium text-slate-900">{s.name}</span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-slate-500">{s.consultations} consult</span>
                        <span className="font-semibold text-emerald-600">₹{(s.revenue || 0).toLocaleString('en-IN')}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-400 py-4">No data for selected period</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Lead Source Breakdown */}
      {adminOverview && (user?.role === 'super_admin' || user?.role === 'finance_admin') && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-slate-900">Lead Funnel Overview</h3>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">{adminOverview.funnel?.total_leads || 0}</p>
                <p className="text-sm text-slate-600">Total Leads</p>
              </div>
              <div className="text-center p-4 bg-violet-50 rounded-lg">
                <p className="text-2xl font-bold text-violet-600">{adminOverview.funnel?.trial_paid || 0}</p>
                <p className="text-sm text-slate-600">Trial Paid</p>
              </div>
              <div className="text-center p-4 bg-amber-50 rounded-lg">
                <p className="text-2xl font-bold text-amber-600">{adminOverview.funnel?.trial_booked || 0}</p>
                <p className="text-sm text-slate-600">Trial Booked</p>
              </div>
              <div className="text-center p-4 bg-emerald-50 rounded-lg">
                <p className="text-2xl font-bold text-emerald-600">{adminOverview.funnel?.trial_completed || 0}</p>
                <p className="text-sm text-slate-600">Trial Done</p>
              </div>
              <div className="text-center p-4 bg-teal-50 rounded-lg">
                <p className="text-2xl font-bold text-teal-600">{adminOverview.funnel?.consultation_completed || 0}</p>
                <p className="text-sm text-slate-600">Consultation</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{adminOverview.funnel?.enrolled || 0}</p>
                <p className="text-sm text-slate-600">Enrolled</p>
              </div>
            </div>
            <div className="mt-4 flex justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                <span className="text-slate-600">Auto-Imported: {adminOverview.leads_overview?.auto_imported || 0}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-violet-500 rounded-full"></span>
                <span className="text-slate-600">Manual: {adminOverview.leads_overview?.manual || 0}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Revenue Breakdown (Admin only) — V33.1: India Trial vs International Trial split,
          Sales Revenue is enrollment-only (never includes trials), per-currency throughout. */}
      {adminOverview?.revenue && (user?.role === 'super_admin' || user?.role === 'finance_admin') && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6" data-testid="revenue-breakdown-v33">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900">Revenue Breakdown</h2>
            <span className="text-xs text-slate-500">India Trial &lt; ₹300 · International Trial &lt; $30 (excl. $29) · Sales Revenue = enrollments only</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Row 1 — Trial Revenue split */}
            <div className="bg-blue-50 rounded-lg p-4" data-testid="card-india-trial-revenue">
              <p className="text-sm text-blue-600 font-medium">India Trial Revenue</p>
              <p className="text-2xl font-bold text-blue-700 mt-1">
                ₹{(adminOverview.revenue.india_trial_revenue_inr ?? adminOverview.revenue.trial_revenue ?? 0).toLocaleString('en-IN')}
              </p>
              <p className="text-xs text-blue-600/70 mt-1">
                {adminOverview.revenue.india_trial_count ?? 0} trial{(adminOverview.revenue.india_trial_count ?? 0) === 1 ? '' : 's'} · INR &lt; ₹300
              </p>
            </div>
            <div className="bg-sky-50 rounded-lg p-4" data-testid="card-international-trial-revenue">
              <p className="text-sm text-sky-600 font-medium">International Trial Revenue</p>
              <p className="text-2xl font-bold text-sky-700 mt-1">
                ${(adminOverview.revenue.international_trial_revenue_usd ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-sky-600/70 mt-1">
                {adminOverview.revenue.international_trial_count ?? 0} trial{(adminOverview.revenue.international_trial_count ?? 0) === 1 ? '' : 's'} · USD &lt; $30 excl. $29
              </p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-4" data-testid="card-sales-revenue">
              <p className="text-sm text-emerald-600 font-medium">Sales Revenue (enrollments only)</p>
              <div className="mt-1 space-y-0.5">
                <p className="text-xl font-bold text-emerald-700">
                  ₹{(adminOverview.revenue.sales_revenue_inr ?? 0).toLocaleString('en-IN')}
                </p>
                <p className="text-base font-semibold text-emerald-600">
                  + ${(adminOverview.revenue.sales_revenue_usd ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <p className="text-xs text-emerald-600/70 mt-1">first enrollment + renewals</p>
            </div>
            {/* Row 2 — First Enrollment / Renewal / Total */}
            <div className="bg-violet-50 rounded-lg p-4" data-testid="card-first-enrollment-revenue">
              <p className="text-sm text-violet-600 font-medium">First Enrollment</p>
              <div className="mt-1 space-y-0.5">
                <p className="text-xl font-bold text-violet-700">
                  ₹{(adminOverview.revenue.first_enrollment_revenue_inr ?? adminOverview.revenue.first_enrollment_revenue ?? 0).toLocaleString('en-IN')}
                </p>
                <p className="text-base font-semibold text-violet-600">
                  + ${(adminOverview.revenue.first_enrollment_revenue_usd ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>
            <div className="bg-fuchsia-50 rounded-lg p-4" data-testid="card-renewal-revenue">
              <p className="text-sm text-fuchsia-600 font-medium">Renewal Revenue</p>
              <div className="mt-1 space-y-0.5">
                <p className="text-xl font-bold text-fuchsia-700">
                  ₹{(adminOverview.revenue.renewal_revenue_inr ?? adminOverview.revenue.renewal_revenue ?? 0).toLocaleString('en-IN')}
                </p>
                <p className="text-base font-semibold text-fuchsia-600">
                  + ${(adminOverview.revenue.renewal_revenue_usd ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>
            <div className="bg-amber-50 rounded-lg p-4" data-testid="card-total-revenue">
              <p className="text-sm text-amber-600 font-medium">Total Revenue (INR-equivalent)</p>
              <p className="text-2xl font-bold text-amber-700 mt-1">
                ₹{(adminOverview.revenue.total_revenue || 0).toLocaleString('en-IN')}
              </p>
              <p className="text-xs text-amber-600/70 mt-1">aggregate · use Revenue by Currency for accurate USD</p>
            </div>
          </div>
        </div>
      )}

      {/* Revenue by Currency (for super_admin and finance_admin) */}
      {(user?.role === 'super_admin' || user?.role === 'finance_admin') && (
        <RevenueByCurrency dateRange={dateRange} />
      )}

      {/* Tracking Health Widget (for super_admin and marketing) */}
      {(user?.role === 'super_admin' || user?.role === 'marketing') && (
        <TrackingHealthWidget />
      )}
    </div>
  );
};

// Revenue by Currency Component
const RevenueByCurrency = ({ dateRange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (dateRange.start_date) params.append('start_date', dateRange.start_date);
      if (dateRange.end_date) params.append('end_date', dateRange.end_date);
      
      const response = await axios.get(`${API_URL}/api/analytics/revenue-by-currency?${params}`, {
        withCredentials: true
      });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching revenue by currency:', error);
    } finally {
      setLoading(false);
    }
  };

  const currencySymbols = {
    INR: '₹',
    USD: '$',
    GBP: '£',
    EUR: '€',
    AED: 'د.إ',
    SGD: 'S$',
    AUD: 'A$',
    CAD: 'C$'
  };

  const currencyColors = {
    INR: 'bg-indigo-500',
    USD: 'bg-green-500',
    GBP: 'bg-purple-500',
    EUR: 'bg-blue-500',
    AED: 'bg-amber-500',
    SGD: 'bg-rose-500',
    AUD: 'bg-cyan-500',
    CAD: 'bg-orange-500'
  };

  if (loading) {
    return (
      <div className="card">
        <div className="card-body flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  if (!data?.currencies?.length) {
    return null;
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="text-lg font-semibold text-slate-900">Revenue by Currency</h3>
        <p className="text-sm text-slate-500">{data.total_transactions} transactions</p>
      </div>
      <div className="card-body">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {data.currencies.map((item) => (
            <div 
              key={item.currency} 
              className="bg-slate-50 rounded-lg p-4 border border-slate-200"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 ${currencyColors[item.currency] || 'bg-slate-500'} rounded-lg flex items-center justify-center text-white font-bold`}>
                  {currencySymbols[item.currency] || item.currency}
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{item.currency}</p>
                  <p className="text-xs text-slate-500">{item.count} payments</p>
                </div>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Total</span>
                  <span className="font-semibold text-slate-900">
                    {currencySymbols[item.currency] || ''}{item.total_amount.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Base</span>
                  <span className="text-slate-700">
                    {currencySymbols[item.currency] || ''}{item.total_base.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">GST</span>
                  <span className="text-slate-700">
                    {currencySymbols[item.currency] || ''}{item.total_gst.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Tracking Health Widget
const TrackingHealthWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('7d');

  useEffect(() => {
    fetchTrackingHealth();
  }, [period]);

  const fetchTrackingHealth = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/analytics/tracking-health?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching tracking health:', error);
    } finally {
      setLoading(false);
    }
  };

  const eventColors = {
    'PageView': '#6366f1',
    'Lead': '#10b981',
    'Purchase': '#f59e0b',
    'InitiateCheckout': '#8b5cf6',
    'ViewContent': '#3b82f6',
    'CompleteRegistration': '#ec4899',
  };

  if (loading) {
    return (
      <div className="card" data-testid="tracking-health-widget">
        <div className="card-body flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  if (!data || data.total_events === 0) {
    return (
      <div className="card" data-testid="tracking-health-widget">
        <div className="card-header flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Radio className="w-5 h-5 text-indigo-600" />
            <h3 className="text-lg font-semibold text-slate-900">Tracking Health</h3>
          </div>
        </div>
        <div className="card-body flex items-center justify-center py-8 text-slate-400">
          <div className="text-center">
            <Activity className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p>No tracking events yet</p>
            <p className="text-sm">Events will appear once the public website starts sending data</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card" data-testid="tracking-health-widget">
      <div className="card-header flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Radio className="w-5 h-5 text-indigo-600" />
          <h3 className="text-lg font-semibold text-slate-900">Tracking Health</h3>
          <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full font-medium">Meta CAPI</span>
        </div>
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
          {['24h', '7d', '30d'].map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              data-testid={`tracking-period-${p}`}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${
                period === p ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {p.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
      <div className="card-body space-y-5">
        {/* Status Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-slate-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-slate-900">{data.total_events}</p>
            <p className="text-xs text-slate-500">Total Events</p>
          </div>
          <div className="bg-emerald-50 rounded-lg p-3 text-center">
            <div className="flex items-center justify-center gap-1">
              <CheckCircle className="w-4 h-4 text-emerald-600" />
              <p className="text-2xl font-bold text-emerald-700">{data.sent}</p>
            </div>
            <p className="text-xs text-emerald-600">Delivered</p>
          </div>
          <div className="bg-red-50 rounded-lg p-3 text-center">
            <div className="flex items-center justify-center gap-1">
              <XCircle className="w-4 h-4 text-red-500" />
              <p className="text-2xl font-bold text-red-600">{data.failed}</p>
            </div>
            <p className="text-xs text-red-500">Failed</p>
          </div>
          <div className={`rounded-lg p-3 text-center ${data.success_rate >= 95 ? 'bg-emerald-50' : data.success_rate >= 80 ? 'bg-amber-50' : 'bg-red-50'}`}>
            <p className={`text-2xl font-bold ${data.success_rate >= 95 ? 'text-emerald-700' : data.success_rate >= 80 ? 'text-amber-700' : 'text-red-700'}`}>
              {data.success_rate}%
            </p>
            <p className="text-xs text-slate-500">Success Rate</p>
          </div>
        </div>

        {/* Events by Type + Top Sources */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Events by Type */}
          <div>
            <p className="text-sm font-medium text-slate-700 mb-2">Events by Type</p>
            <div className="space-y-2">
              {data.events_by_type.map(evt => {
                const pct = Math.round(evt.count / data.total_events * 100);
                return (
                  <div key={evt.name} className="flex items-center gap-2">
                    <span className="text-xs font-medium text-slate-600 w-28 truncate">{evt.name}</span>
                    <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${pct}%`, backgroundColor: eventColors[evt.name] || '#94a3b8' }}
                      />
                    </div>
                    <span className="text-xs text-slate-500 w-10 text-right">{evt.count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top Sources */}
          <div>
            <p className="text-sm font-medium text-slate-700 mb-2">Top Traffic Sources</p>
            {data.top_sources.length > 0 ? (
              <div className="space-y-2">
                {data.top_sources.map((src, idx) => (
                  <div key={src.source} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                    <span className="text-sm text-slate-700 font-medium">{src.source}</span>
                    <span className="text-sm font-semibold text-indigo-600">{src.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400 py-4 text-center">No source data yet</p>
            )}
          </div>
        </div>

        {/* Recent Events */}
        {data.recent_events.length > 0 && (
          <div>
            <p className="text-sm font-medium text-slate-700 mb-2">Recent Events</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                    <th className="pb-2 font-medium">Event</th>
                    <th className="pb-2 font-medium">Status</th>
                    <th className="pb-2 font-medium">Lead</th>
                    <th className="pb-2 font-medium">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_events.slice(0, 5).map((evt, idx) => (
                    <tr key={evt.event_id || idx} className="border-b border-slate-50">
                      <td className="py-1.5">
                        <span className="inline-flex items-center gap-1">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: eventColors[evt.event_name] || '#94a3b8' }} />
                          {evt.event_name}
                        </span>
                      </td>
                      <td className="py-1.5">
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                          evt.send_status === 'sent' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                        }`}>{evt.send_status}</span>
                      </td>
                      <td className="py-1.5 text-slate-500 font-mono text-xs">{evt.lead_id || '-'}</td>
                      <td className="py-1.5 text-slate-400 text-xs">{evt.created_at ? new Date(evt.created_at).toLocaleString('en-IN', {hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short'}) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MetricCard = ({ title, value, subtitle, icon, highlight }) => (
  <KpiCard title={title} value={value} subtitle={subtitle} icon={icon} highlight={highlight} tone={highlight ? 'primary' : 'default'} />
);

export default Dashboard;
